import { useState, useCallback } from 'react';
import billingApiService, { FeatureUsageResponse, BillingPayment } from '../services/billingApi';
import { Invoice } from '../types/profile';

/**
 * Hook de facturation — customer-facing uniquement
 * Wraps : GET /billing/invoices, /billing/invoices/:id/detail,
 * /billing/invoices/:id/pdf, /billing/payments, /features/usage
 */
export function useBillingApi() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApiCall = useCallback(async <T>(
    apiCall: () => Promise<T>
  ): Promise<T | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiCall();
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Une erreur est survenue';
      setError(errorMessage);
      console.error('Billing API Error:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ===== FACTURES =====

  const getInvoices = useCallback(async (params?: {
    page?: number;
    limit?: number;
    status?: 'draft' | 'issued' | 'paid' | 'overdue' | 'cancelled';
  }): Promise<{
    invoices: Invoice[];
    meta: { pagination: { page: number; limit: number; total: number; pages: number } };
  } | null> => {
    return handleApiCall(() => billingApiService.getInvoices(params));
  }, [handleApiCall]);

  const getInvoiceDetail = useCallback(async (invoiceId: string): Promise<Invoice | null> => {
    return handleApiCall(() => billingApiService.getInvoiceDetail(invoiceId));
  }, [handleApiCall]);

  const downloadInvoicePdf = useCallback(async (invoiceId: string): Promise<Blob | null> => {
    return handleApiCall(() => billingApiService.downloadInvoicePdf(invoiceId));
  }, [handleApiCall]);

  // ===== PAIEMENTS =====

  const getPayments = useCallback(async (params?: { page?: number; limit?: number }): Promise<{
    payments: BillingPayment[];
    total: number;
    page: number;
    limit: number;
  } | null> => {
    return handleApiCall(() => billingApiService.getPayments(params));
  }, [handleApiCall]);

  // ===== UTILISATION DES FEATURES =====

  const getFeatureUsage = useCallback(async (): Promise<FeatureUsageResponse | null> => {
    return handleApiCall(() => billingApiService.getFeatureUsage());
  }, [handleApiCall]);

  return {
    isLoading,
    error,
    getInvoices,
    getInvoiceDetail,
    downloadInvoicePdf,
    getPayments,
    getFeatureUsage
  };
}
